import React, { Fragment } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import Header from '../Header';
import Footer from '../Footer';
import Anchor from '../Anchor';
import LivePerson from '../LivePerson';
import MaintenanceMessage from '../MaintenanceMessage';
import ThemeProviderWrapper from '../ThemeProviderWrapper';
import rrpHeadersPages from '../../constants/rrpHeadersPages';
import './layout.css';

import { shoppingCartUrl } from '../../config/links';
import isFeatureFlagEnabled from '../../helpers/feature-flags';
import { FULLSTORY_ENABLE } from '../../types/featureFlag.types';
import FullStory from 'react-fullstory';
import useSessionTimer from '../../hooks/useSessionTimer';
import SessionWarningModal from '../SessionWarningModal';
import { useTracker } from '../../hooks/useTracker';
import useInterceptor from '../../hooks/useInterceptor';

const Site = styled.div`
  display: flex;
  min-height: 100vh;
  flex-direction: column;
`;

const Container = styled.div`
  width: 100%;
  flex-grow: 1;
  background-color: ${p => p.theme.colours.page_background_pri};
  ${({ theme }) =>
    theme &&
    theme.colours.page_text_colour_pri &&
    css`
      color: ${theme.colours.page_text_colour_pri};
    `}
  ${p => p.theme.media.md`
  padding-top: ${p.theme.spacing(4)}
  `};
  ${p => p.theme.media.sm`
    margin-top: ${p.theme.spacing(2)};
  `}
  margin: 0 auto;
`;

const Layout = ({ children, location }) => {
  const { cisInformation } = useSelector(state => {
    return {
      cisInformation: state.user?.cis?.information
    };
  });
  const isCisFlow = Boolean(cisInformation?.rates);

  const {
    popUpFlag,
    onCloseSessionWarning,
    onExtendSessionWarning
  } = useSessionTimer(location);
  useTracker(location);
  useInterceptor();
  const cookieText = (
    <Fragment>
      By using our website, you agree to our use of cookies to make your
      browsing experience a better one.{' '}
      <Anchor
        href="https://www.singtel.com/data-protection/cookie-policy"
        target="_blank"
        rel="noopener"
      >
        Read
      </Anchor>{' '}
      about our Cookie Policy.
    </Fragment>
  );
  return (
    <ThemeProviderWrapper>
      <Site>
        <MaintenanceMessage location={location} />
        <Header
          baseUrl={process.env.GATSBY_AEM_URL}
          shoppingCartUrl={shoppingCartUrl}
          isRrpHeader={rrpHeadersPages.includes(
            location.pathname.replace(/\/$/, '')
          )}
        />
        {isFeatureFlagEnabled(FULLSTORY_ENABLE) && (
          <FullStory org={process.env.GATSBY_FULLSTORY_ORG_ID} />
        )}
        <Container data-testid="container" isCisFlow={isCisFlow}>
          {children}
        </Container>
        <Footer
          copyrightText="&copy; Singtel (CRN: 199201624D) All Rights Reserved."
          cookieText={cookieText}
          links={[
            {
              text: 'DATA PROTECTION',
              link: 'https://www.singtel.com/personal/i/dataprotection'
            },
            { text: 'CAREER', link: 'https://start.singtel.com/' },
            {
              text: 'TERMS OF USE',
              link: 'https://www.singtel.com/standard-agreement'
            },
            {
              text: 'COPYRIGHT NOTICES',
              link: 'https://www.singtel.com/copyright'
            }
          ]}
        />
        <LivePerson
          lpDivId="lpButtonDiv"
          lpEngagementId={process.env.GATSBY_LIVEPERSON_ENGAGEMENT_ID}
          lpSection={process.env.GATSBY_LIVEPERSON_SECTION}
          lpSiteId={process.env.GATSBY_LIVEPERSON_SITE_ID}
        />
        {popUpFlag && (
          <SessionWarningModal
            showModal={popUpFlag}
            closeSessionWarning={onCloseSessionWarning}
            extendSessionWarning={onExtendSessionWarning}
          />
        )}
      </Site>
    </ThemeProviderWrapper>
  );
};

Layout.propTypes = {
  children: PropTypes.node.isRequired
};

export default Layout;
